import type { Ctx, MilkdownPlugin } from '@milkdown/kit/ctx';
import { createTimer } from '@milkdown/kit/ctx';
import { nodesCtx, schemaTimerCtx } from '@milkdown/kit/core';
import type { Node as PMNode } from '@milkdown/kit/prose/model';
import { tableCellSchema, tableHeaderRowSchema, tableHeaderSchema, tableRowSchema, tableSchema } from '@milkdown/kit/preset/gfm';
import { Plugin, PluginKey } from '@milkdown/kit/prose/state';
import type { NodeSchema } from '@milkdown/kit/transformer';
import { $prose } from '@milkdown/kit/utils';

import {
  normalizePlatformTableNode,
  stripLiteralBrFromTableNode,
  tableContainsPlatformRows,
  type PlatformTableNormalizeTypes,
} from './normalizePlatformTable';

export { platformTableCompatNodes } from './tableCompatNodes';
export {
  normalizePlatformTableNode,
  stripLiteralBrFromTableNode,
  tableContainsPlatformRows,
  type PlatformTableNormalizeTypes,
} from './normalizePlatformTable';

/** Must match Milkdown GFM `table` id so we can overwrite Crepe's entry before Schema builds. */
const TABLE_NODE_ID = 'table';

/**
 * Allow Platform `tr` rows inside `table` so y-prosemirror can hydrate post-publish
 * state from `markdownToYDoc`. Milkdown GFM only allows `table_header_row table_row+`;
 * `schema.node()` then throws and y-prosemirror **deletes** the table from the Y.Doc.
 */
export const PLATFORM_TABLE_CONTENT = '(tr | table_header_row | table_row)+';

const PlatformTableSchemaReady = createTimer('PlatformTableSchemaReady');

function replaceTableInNodesCtx(ctx: Ctx, nodeSchema: NodeSchema): void {
  const entry: [string, NodeSchema] = [TABLE_NODE_ID, nodeSchema];
  ctx.update(nodesCtx, (ns) => [...ns.filter(([id]) => id !== TABLE_NODE_ID), entry]);
}

/**
 * Widen GFM `table` content to accept Platform rows before Schema is built.
 * Same nodesCtx-overwrite pattern as platform image-block — avoid `extendSchema()`
 * (second `$node('table')` races under `Promise.all`).
 */
export const platformTableContentSchema: MilkdownPlugin = (ctx) => {
  ctx.record(PlatformTableSchemaReady);
  ctx.update(schemaTimerCtx, (timers) => timers.concat(PlatformTableSchemaReady));

  return async () => {
    for (let attempts = 0; attempts < 100; attempts++) {
      if (ctx.get(nodesCtx).some(([id]) => id === TABLE_NODE_ID)) break;
      await Promise.resolve();
    }

    try {
      const prevFactory = ctx.get(tableSchema.key);
      const nextFactory = (c: Ctx): NodeSchema => {
        const base = prevFactory(c);
        
return { ...base, content: PLATFORM_TABLE_CONTENT };
      };
      ctx.set(tableSchema.key, nextFactory);
      replaceTableInNodesCtx(ctx, nextFactory(ctx));
    } catch {
      const existing = ctx.get(nodesCtx).find(([id]) => id === TABLE_NODE_ID);
      if (existing) {
        replaceTableInNodesCtx(ctx, { ...existing[1], content: PLATFORM_TABLE_CONTENT });
      }
    }

    ctx.done(PlatformTableSchemaReady);

    return () => {
      ctx.update(schemaTimerCtx, (timers) => timers.filter((t) => t !== PlatformTableSchemaReady));
      ctx.clearTimer(PlatformTableSchemaReady);
    };
  };
};

const PLATFORM_TABLE_NORMALIZER_META = 'platformTableNormalizer';
const platformTableNormalizerKey = new PluginKey('platformTableNormalizer');

/**
 * After Y.js hydrates Platform-shaped tables, rewrite them to Milkdown GFM nodes
 * so table editing/rendering plugins work.
 */
export const platformTableNormalizer = $prose((ctx) => {
  const types: PlatformTableNormalizeTypes = {
    tableHeaderRow: tableHeaderRowSchema.type(ctx),
    tableRow: tableRowSchema.type(ctx),
    tableHeader: tableHeaderSchema.type(ctx),
    tableCell: tableCellSchema.type(ctx),
  };

  return new Plugin({
    key: platformTableNormalizerKey,
    appendTransaction(transactions, _oldState, newState) {
      if (transactions.some((txn) => txn.getMeta(PLATFORM_TABLE_NORMALIZER_META))) {
        return null;
      }

      const replacements: { from: number; to: number; node: PMNode }[] = [];

      newState.doc.descendants((node, pos) => {
        if (node.type.name !== 'table') {
          return;
        }

        let next: PMNode | null = null;
        if (tableContainsPlatformRows(node)) {
          next = normalizePlatformTableNode(node, types);
        }
        next = stripLiteralBrFromTableNode(next ?? node) ?? next;

        if (next) {
          replacements.push({ from: pos, to: pos + node.nodeSize, node: next });
        }
      });

      if (replacements.length === 0) {
        return null;
      }

      const txn = newState.tr;
      replacements.reverse().forEach(({ from, to, node }) => {
        txn.replaceWith(from, to, node);
      });
      txn.setMeta(PLATFORM_TABLE_NORMALIZER_META, true);

      return txn;
    },
  });
});

export const platformTablePlugins = [platformTableNormalizer] as unknown as MilkdownPlugin[];
